import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TabsContent } from "@/components/ui/tabs";
import { JobFormData } from "./types/jobTypes";
import { useJobForm } from "./hooks/useJobForm";
import { BasicInformationTab } from "./form-tabs/BasicInformationTab";
import { JobDetailsTab } from "./form-tabs/JobDetailsTab";
import { QualificationsTab } from "./form-tabs/QualificationsTab";
import { AdditionalInfoTab } from "./form-tabs/AdditionalInfoTab";
import { FormTabs } from "./form-tabs/FormTabs";
import { WizardTimeline } from "./WizardTimeline";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { ProcessingOverlay } from "@/components/ui/ProcessingOverlay";
import { useAuth } from "@/providers/AuthProvider";
import { LanguageManager } from "@/components/ui/LanguageManager";
import { JobLanguage } from "@/types/candidate";

interface JobOffersAddWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onAddJob: (jobData: any) => Promise<string | undefined>;
  initialJobData?: Partial<JobFormData>;
}

export const JobOffersAddWizard = ({
  isOpen,
  onClose,
  onAddJob,
  initialJobData = {}
}: JobOffersAddWizardProps) => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const [languages, setLanguages] = useState<JobLanguage[]>([]);
  
  // Using the same job form hook as the edit modal
  const {
    jobData,
    setJobData,
    isSubmitting,
    setIsSubmitting,
    activeTab,
    setActiveTab,
    selectedLanguages,
    showOtherLanguageField,
    showBudgetPerm,
    showBudgetFreelance,
    handleSelectCompany,
    handleSelectContact,
    handleSkillsKeyDown,
    handleLanguageChange,
    handleEmploymentTypeChange,
    handleTimeCommitmentChange,
    handleInputChange,
    handleSelectChange,
    handleSubmit,
    handlePasteFromClipboard,
    isEmploymentTypeSelected,
    resetForm
  } = useJobForm(undefined, async (data) => {
    // Create mode - no job ID needed
    try {
      // Process the form data before sending it to the API
      const processedData = {
        // Basic information
        title: data.title,
        company_id: data.companyId,
        description: data.description,
        skills: data.skills ? data.skills.split(',').map(skill => skill.trim()) : [],
        contact_id: data.contactId,
        status: (data.status && String(data.status).trim()) ? String(data.status).trim() : "open",
        
        // Process mapped field names for the API
        employment_type: data.employmentType,
        time_commitment: data.timeCommitment,
        contract_duration: data.contractDuration,
        start_date: data.startDate,
        end_date: data.endDate,
        seniority_level: data.seniorityLevel,
        budget: data.budget,
        budget_freelance: data.budgetFreelance,
        location_type: data.locationType,
        workplace_breakdown: data.workplaceBreakdown,
        hard_skills: data.hardSkills ? data.hardSkills.split(',').map(skill => skill.trim()) : [],
        soft_skills: data.softSkills ? data.softSkills.split(',').map(skill => skill.trim()) : [],
        education_level: data.educationLevel,
        years_of_experience: data.yearsOfExperience,
        language_requirements: data.languageRequirements,
        extra_lang: data.extraLang,
        culture_tags: data.cultureTags ? data.cultureTags.split(',').map(tag => tag.trim()) : [],
        application_deadline: data.applicationDeadline,
        job_posting_date: data.jobPostingDate,
        source: data.source,
        assigned_recruiter: data.assignedRecruiter,
        employment_classification: data.employmentClassification,
        ai_summary: data.aiSummary,
        // Pass languages so the parent can insert them before navigating
        _job_languages: languages.map(lang => ({
          language: lang.language,
          language_code: lang.language_code,
          min_cefr_level: lang.min_cefr_level,
          is_mandatory: lang.is_mandatory,
          is_user_owned: lang.is_user_owned
        }))
      };
      
      const result = await onAddJob(processedData);
      
      if (result) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Error in job creation:", error);
      throw error;
    }
  });

  // Apply initial job data when component mounts or initialJobData changes
  useEffect(() => {
    if (isOpen && initialJobData && Object.keys(initialJobData).length > 0) {
      //console.log("Setting initial job data:", initialJobData);
      setJobData(prevData => ({
        ...prevData,
        ...initialJobData,
        // Ensure status always has a valid value
        status: initialJobData.status || prevData.status || "open"
      }));

      // Populate language state from pre-scanned languages if provided
      if ((initialJobData as any)._job_languages?.length > 0) {
        const initialLangs: JobLanguage[] = (initialJobData as any)._job_languages.map((lang: any) => ({
          id: '',
          job_id: '',
          language: lang.language,
          language_code: lang.language_code,
          min_cefr_level: lang.min_cefr_level ?? 0,
          is_mandatory: lang.is_mandatory ?? true,
          is_user_owned: lang.is_user_owned ?? false,
          created_at: new Date().toISOString()
        }));
        setLanguages(initialLangs);
      }
    }
  }, [isOpen, initialJobData, setJobData]);

  useEffect(() => {
    if (isOpen && userProfile?.id && !jobData.assignedRecruiter) {
      setJobData((prev) => ({ ...prev, assignedRecruiter: userProfile.id }));
    }
  }, [isOpen, userProfile?.id]);

  // Reset languages when dialog is closed
  useEffect(() => {
    if (!isOpen) {
      setLanguages([]);
    }
  }, [isOpen]);

  // Custom form submit handler for adding a new job
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Add guard against concurrent submissions
    if (isSubmitting) {
      return;
    }
    
    // Only submit if it's an explicit save or last step submission 
    let isExplicitSave = false
    let isSubmitButtonClick = false;
    
    if (e.nativeEvent?._isSubmitButtonClick) {
      isSubmitButtonClick = true;
    }

    if (e.nativeEvent?._isExplicitSave)
      isExplicitSave = true;
    
    if (isExplicitSave || isSubmitButtonClick) {
      try {
        setIsSubmitting(true);
        const success = await handleSubmit(e);
        
        if (success) {
          toast({
            title: t("common.success"),
            description: t("jobs.formWizard.createSuccess")
          });
          setLanguages([]); // Reset languages
          onClose();
          return;
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        toast({
          title: t("common.error"),
          description: t("jobs.formWizard.createError") + (error instanceof Error ? ": " + error.message : ""),
          variant: "destructive"
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const steps = [
    t("jobs.formWizard.chooseMethod"), 
    t("jobs.formWizard.basicInfo"), 
    t("jobs.formWizard.jobDetails"), 
    t("jobs.formWizard.qualifications"), 
    t("jobs.formWizard.additionalInfo")
  ];
  
  const stepIndex = {
    "basic": 1,
    "details": 2,
    "qualifications": 3,
    "metadata": 4
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      // Only allow closing via the explicit onClose handler
      if (!open) onClose();
    }}>
      <DialogContent 
        className="w-[95vw] max-w-[95vw] max-h-[90vh] h-[90vh] flex flex-col p-0 overflow-hidden" 
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <ProcessingOverlay 
          isProcessing={isSubmitting}
          message={t("jobs.jobOfferModal.creatingJobOffer")}
        />
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="text-xl font-semibold">{t("jobs.formWizard.title")}</DialogTitle>
        </DialogHeader>
        
        <div className="px-6 pt-4 flex-1 min-h-0 flex flex-col overflow-hidden">
          <WizardTimeline 
            steps={steps} 
            currentStep={stepIndex[activeTab as keyof typeof stepIndex]} 
          />
          
          <div className="mt-6 flex-1 min-h-0 overflow-hidden">
            <FormTabs 
              activeTab={activeTab} 
              setActiveTab={setActiveTab}
              onCancel={onClose}
              onSubmit={handleFormSubmit}
              isSubmitting={isSubmitting}
              isCreateMode={true}
            >
              <TabsContent value="basic">
                <BasicInformationTab 
                  jobData={jobData}
                  handleInputChange={handleInputChange}
                  handleSelectChange={handleSelectChange}
                  handleSelectCompany={handleSelectCompany}
                  handleSelectContact={handleSelectContact}
                  handlePasteFromClipboard={handlePasteFromClipboard}
                  handleSkillsKeyDown={handleSkillsKeyDown}
                  handleEmploymentTypeChange={handleEmploymentTypeChange}
                  isEmploymentTypeSelected={isEmploymentTypeSelected}
                />
              </TabsContent>
              
              <TabsContent value="details">
                <JobDetailsTab
                  jobData={jobData}
                  handleInputChange={handleInputChange}
                  handleSkillsKeyDown={handleSkillsKeyDown}
                  handleEmploymentTypeChange={handleEmploymentTypeChange}
                  handleTimeCommitmentChange={handleTimeCommitmentChange}
                  isEmploymentTypeSelected={isEmploymentTypeSelected}
                  showBudgetPerm={showBudgetPerm}
                  showBudgetFreelance={showBudgetFreelance}
                />
              </TabsContent>
              
              <TabsContent value="qualifications">
                <div className="space-y-6">
                  <QualificationsTab
                    jobData={jobData}
                    handleInputChange={handleInputChange}
                    handleSkillsKeyDown={handleSkillsKeyDown}
                  />
                  
                  {/* Languages Section */}
                  <div className="space-y-2">
                    <LanguageManager
                      languages={languages.map(lang => ({
                        id: lang.id,
                        language: lang.language,
                        language_code: lang.language_code,
                        level: lang.min_cefr_level,
                        is_user_owned: lang.is_user_owned,
                        is_mandatory: lang.is_mandatory
                      }))}
                      onChange={(updatedLanguages) => {
                        const newLanguages: JobLanguage[] = updatedLanguages.map(lang => ({
                          id: lang.id || '',
                          job_id: '', // Will be set when job is created
                          language: lang.language,
                          language_code: lang.language_code,
                          min_cefr_level: lang.level,
                          is_mandatory: lang.is_mandatory || false,
                          is_user_owned: lang.is_user_owned,
                          created_at: new Date().toISOString()
                        }));
                        setLanguages(newLanguages);
                      }}
                      type="job"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="metadata">
                <AdditionalInfoTab
                  jobData={jobData}
                  handleInputChange={handleInputChange}
                />
              </TabsContent>
            </FormTabs>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
